import { useMemo } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { CachedDataNotice } from '@tov-plus/pwa';
import { TovButton, formatEnumLabel, msg, TovCard } from '@tov-plus/ui';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import {
  readGuestSnapshot,
  writeGuestSnapshot,
} from '../lib/offline/cacheSnapshot';
function getTravelPacketCacheKey(occasionSlug: string, guestToken: string) {
  return `tov-plus:guest-travel-packet:${occasionSlug}:${guestToken}`;
}

const travelPacketErrorMessage = msg(
  'pages.travel_packet_page.travel_packet_could_not_be_loaded',
);

type TravelPacketLocationResponse = Awaited<
  ReturnType<typeof guestApi.listVisibleLocations>
>;

function getLocationTypePriority(
  type: TravelPacketLocationResponse['locations'][number]['type'],
) {
  switch (type) {
    case 'venue':
      return 0;
    case 'transportation_point':
      return 1;
    case 'parking':
      return 2;
    case 'accommodation':
      return 3;
    case 'internal_venue_point':
      return 4;
    case 'point_of_interest':
      return 5;
    case 'custom':
      return 6;
  }
}

export function TravelPacketPage() {
  const access = useGuestAccess();
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const travelPacketQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.travelPacket(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.travelPacket('pending', 'pending'),
    queryFn: async () => {
      const occasionSlug = readyOccasionSlug!;
      const guestToken = readyGuestToken!;
      const cacheKey = getTravelPacketCacheKey(occasionSlug, guestToken);
      try {
        const response = await guestApi.listVisibleLocations(
          occasionSlug,
          guestToken,
        );
        await writeGuestSnapshot(cacheKey, {
          locations: response,
          cachedAt: new Date().toISOString(),
        });
        return { locations: response, cached: false };
      } catch (error) {
        const cached = await readGuestSnapshot<{
          locations: Awaited<ReturnType<typeof guestApi.listVisibleLocations>>;
        }>(cacheKey);
        if (cached?.locations) {
          return { locations: cached.locations, cached: true };
        }
        throw error;
      }
    },
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const locations = travelPacketQuery.data?.locations ?? null;
  const errorMessage = travelPacketQuery.data?.cached
    ? msg(
        'pages.travel_packet_page.offline_travel_packet_loaded_from_cache_reconnect_for_the_latest_venue_updates',
      )
    : travelPacketQuery.isError
      ? getGuestErrorMessage(travelPacketQuery.error, travelPacketErrorMessage)
      : null;
  const packetSummary = useMemo(() => {
    const locationItems = locations?.locations ?? [];
    return {
      mapped: locations?.mapMarkers.length ?? 0,
      contacts: locations?.contacts.length ?? 0,
      directions:
        locations?.locationLinks.filter((item) =>
          ['google_maps', 'waze', 'rideshare'].includes(item.link_type),
        ).length ?? 0,
      eventLinked: new Set(locations?.eventLinks.map((item) => item.event_id))
        .size,
      parking: locationItems.filter((item) => item.type === 'parking').length,
      transportation: locationItems.filter(
        (item) => item.type === 'transportation_point',
      ).length,
      stays: locationItems.filter((item) => item.type === 'accommodation')
        .length,
    };
  }, [locations]);
  const sortedLocations = useMemo(() => {
    return [...(locations?.locations ?? [])].sort((left, right) => {
      const priority =
        getLocationTypePriority(left.type) -
        getLocationTypePriority(right.type);
      return priority || left.title.localeCompare(right.title);
    });
  }, [locations]);
  const packetText = useMemo(() => {
    if (!locations || access.status !== 'ready') {
      return '';
    }
    const lines = [
      `${access.experience.occasion.displayName} ${msg('pages.travel_packet_page.travel_packet')}`,
      `${msg('pages.travel_packet_page.guest')}: ${access.experience.guestDisplayName}`,
      '',
    ];
    for (const location of locations.locations) {
      const contacts = locations.contacts.filter(
        (item) => item.location_id === location.id,
      );
      const links = locations.locationLinks.filter(
        (item) => item.location_id === location.id,
      );
      const mapLinks = links.filter((item) =>
        ['google_maps', 'waze', 'rideshare'].includes(item.link_type),
      );
      const eventCount = new Set(
        locations.eventLinks
          .filter((item) => item.location_id === location.id)
          .map((item) => item.event_id),
      ).size;
      lines.push(location.title);
      lines.push(
        `${msg('pages.travel_packet_page.address')}: ${
          location.addressText ??
          msg('pages.travel_packet_page.address_pending')
        }`,
      );
      lines.push(
        `${msg('pages.travel_packet_page.type')}: ${formatEnumLabel(location.type)}`,
      );
      lines.push(
        eventCount > 0
          ? `${msg('pages.travel_packet_page.published_for')} ${eventCount} ${
              eventCount === 1
                ? msg('pages.travel_packet_page.event')
                : msg('pages.travel_packet_page.events')
            }`
          : msg('pages.travel_packet_page.general_occasion_location'),
      );
      lines.push(
        mapLinks.length > 0
          ? `${msg('pages.travel_packet_page.directions')}: ${mapLinks
              .map((item) => `${item.label} (${item.url})`)
              .join(', ')}`
          : msg('pages.travel_packet_page.directions_pending'),
      );
      lines.push(
        contacts.length > 0
          ? `${msg('pages.travel_packet_page.contacts')}: ${contacts
              .map((item) => `${item.label} (${item.value})`)
              .join(', ')}`
          : msg('pages.locations_page.contacts_none_published'),
      );
      lines.push(
        links.length > 0
          ? `${msg('pages.travel_packet_page.links')}: ${links
              .map((item) => `${item.label} (${item.url})`)
              .join(', ')}`
          : msg('pages.locations_page.links_none_published'),
      );
      lines.push('');
    }
    return lines.join('\n');
  }, [access, locations]);
  if (access.status !== 'ready' || travelPacketQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (travelPacketQuery.isError) {
    return (
      <StatePanel
        preset="access-unavailable"
        detail={errorMessage}
        actions={
          <>
            <TovButton onClick={() => void travelPacketQuery.refetch()}>
              {msg('pages.travel_packet_page.retry_travel_packet')}
            </TovButton>
            <TovButton asChild variant="secondary">
              <NavLink to="/locations">
                {msg(
                  'pages.travel_packet_page.pages_travel_packet_page_open_locations',
                )}
              </NavLink>
            </TovButton>
          </>
        }
      />
    );
  }
  return (
    <div className="page-grid">
      {errorMessage ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.today_page.pages_today_page_offline_continuity')}
          </p>
          <p>{errorMessage}</p>
        </TovCard>
      ) : null}
      <CachedDataNotice cachedValue={locations} />
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_travel_packet')}
        </p>
        <h2>
          {msg(
            'pages.travel_packet_page.pages_travel_packet_page_mobile_day_of_travel_reference',
          )}
        </h2>
        <p>
          {msg(
            'pages.travel_packet_page.pages_travel_packet_page_this_packet_is_optimized_for_quick_scrolling_printing',
          )}{' '}
        </p>
        <div className="action-row">
          <TovButton
            variant="secondary"
            onClick={() => {
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                void navigator.clipboard.writeText(packetText);
              }
            }}
          >
            {msg(
              'pages.travel_packet_page.pages_travel_packet_page_copy_packet',
            )}{' '}
          </TovButton>
          <TovButton variant="secondary" onClick={() => window.print()}>
            {msg(
              'pages.travel_packet_page.pages_travel_packet_page_print_packet',
            )}{' '}
          </TovButton>
          <TovButton asChild variant="secondary">
            <a
              href={`mailto:?subject=${encodeURIComponent('Travel packet')}&body=${encodeURIComponent(packetText)}`}
            >
              {msg(
                'pages.locations_page.pages_locations_page_email_packet',
              )}{' '}
            </a>
          </TovButton>
          <TovButton asChild>
            <NavLink to="/locations">
              {msg(
                'pages.travel_packet_page.pages_travel_packet_page_open_locations',
              )}
            </NavLink>
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.travel_packet_page.day_of_snapshot')}
        </p>
        <div className="detail-list">
          <article>
            <strong>{packetSummary.eventLinked}</strong>
            <span>{msg('pages.travel_packet_page.event_linked_stops')}</span>
          </article>
          <article>
            <strong>{packetSummary.directions}</strong>
            <span>{msg('pages.travel_packet_page.direction_links')}</span>
          </article>
          <article>
            <strong>{packetSummary.contacts}</strong>
            <span>{msg('pages.travel_packet_page.travel_contacts')}</span>
          </article>
          <article>
            <strong>{packetSummary.mapped}</strong>
            <span>{msg('pages.travel_packet_page.map_points')}</span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.travel_packet_page.route_planning')}
        </p>
        <div className="detail-list">
          <article>
            <strong>{packetSummary.transportation}</strong>
            <span>
              {msg('pages.travel_packet_page.shuttle_pickup_or_dropoff_points')}
            </span>
          </article>
          <article>
            <strong>{packetSummary.parking}</strong>
            <span>{msg('pages.travel_packet_page.parking_locations')}</span>
          </article>
          <article>
            <strong>{packetSummary.stays}</strong>
            <span>{msg('pages.travel_packet_page.accommodation_options')}</span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.travel_packet_page.location_cards')}
        </p>
        <div className="detail-list detail-list--stacked">
          {sortedLocations.map((location) => {
            const contacts = locations?.contacts.filter(
              (item) => item.location_id === location.id,
            );
            const links = locations?.locationLinks.filter(
              (item) => item.location_id === location.id,
            );
            const eventCount = new Set(
              locations?.eventLinks
                .filter((item) => item.location_id === location.id)
                .map((item) => item.event_id),
            ).size;
            return (
              <article key={location.id}>
                <strong>{location.title}</strong>
                <span>{formatEnumLabel(location.type)}</span>
                <span>
                  {location.addressText ??
                    msg('pages.travel_packet_page.address_pending')}
                </span>
                <span>
                  {eventCount > 0
                    ? `${eventCount} ${msg('pages.travel_packet_page.linked_events')}`
                    : msg('pages.travel_packet_page.general_occasion_location')}
                </span>
                {location.description ? (
                  <span>{location.description}</span>
                ) : null}
                {links && links.length > 0 ? (
                  <div className="action-row">
                    {links.map((link) => (
                      <TovButton
                        asChild
                        key={link.id}
                        size="sm"
                        variant="secondary"
                      >
                        <a href={link.url}>{link.label}</a>
                      </TovButton>
                    ))}
                  </div>
                ) : null}
                {contacts && contacts.length > 0 ? (
                  <span>
                    {contacts
                      .map((contact) => `${contact.label}: ${contact.value}`)
                      .join(' | ')}
                  </span>
                ) : null}
              </article>
            );
          })}
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg(
            'pages.travel_packet_page.pages_travel_packet_page_offline_friendly_copy',
          )}
        </p>
        <pre className="travel-packet__copy">{packetText}</pre>
      </TovCard>
    </div>
  );
}
